// ── Password Reset Page (TPT194) ──
//
// Public landing page for the emailed link `#/reset-password/<token>` (built by
// api/src/lib/mailer.js's sendPasswordResetEmail(): a hash route with the token
// as a PATH SEGMENT, not a query param). Structural twin of pages/register.js:
// public (no checkAuth()), paints straight into #app after teardownLayout().
//
// Unlike register.js/invite.js it deliberately does NOT bounce a signed-in
// visitor to '/'. Someone already signed in in this browser who clicks a reset
// link they asked for must still be able to set the new password — bouncing
// them would make the emailed link silently do nothing.
//
// Flow: the token is shape-checked locally (a truncated/mangled link renders
// the dead-link state with no round trip), then a new-password + confirm form
// calls POST /auth/password/reset. The server issues NO session on success, so
// this hands back to #/login with a toast; it never signs anyone in, and it
// ends any session already open in this browser (see logout() below).
//
// Password length is validated client-side BEFORE the request on purpose: the
// server checks the password before it ever looks at the token, so a short
// password submitted against a dead link would otherwise come back as a
// password error and mislead the user into thinking the link is fine.

import { resetPassword, logout } from '../auth.js';
import { navigate } from '../router.js';
import { teardownLayout } from '../layout.js';
import { showToast, esc } from '../ui.js';
import { normalizeResetTokenInput, isDeadResetLink, resetPasswordErrorText } from '../lib/signup-form.js';
import { passwordToggleButton, bindPasswordToggle } from '../lib/password-toggle.js';
import { startPasswordReset } from './login.js';
import { brandLockup } from '../lib/brand.js';

// Mirrors the server's passwordError() rule (api/src/routes/auth.js).
const MIN_PASSWORD_LENGTH = 8;

function renderShell(bodyHTML) {
  const app = document.getElementById('app');
  app.innerHTML = `<div class="login-container">${brandLockup()}<div class="auth-card">${bodyHTML}</div></div>`;
  return app;
}

// One dead-link state for every failure the server can't (and won't) tell
// apart: malformed, unknown, expired, already used. The way out is a fresh
// link, so that is the primary action.
function renderDeadLink(message) {
  const app = renderShell(`
    <p>${esc(message)}</p>
    <button type="button" class="btn btn-primary btn-block" id="reset-request-new">Request a new link</button>
    <div class="login-switch"><a href="#/login">Back to sign in</a></div>`);

  app.querySelector('#reset-request-new').addEventListener('click', () => {
    startPasswordReset();
    navigate('/login');
  });
}

function renderForm(token) {
  const app = renderShell(`
    <p>Choose a new password for your account.</p>
    <form class="login-form" id="reset-form">
      <div class="form-group">
        <label class="form-label" for="reset-password">New password</label>
        <div class="password-field has-toggle">
          <input class="form-input" id="reset-password" type="password" autocomplete="new-password" minlength="${MIN_PASSWORD_LENGTH}" required autofocus>
          ${passwordToggleButton('reset-password-toggle')}
        </div>
      </div>
      <div class="form-group">
        <label class="form-label" for="reset-confirm">Confirm new password</label>
        <div class="password-field">
          <input class="form-input" id="reset-confirm" type="password" autocomplete="new-password" minlength="${MIN_PASSWORD_LENGTH}" required>
        </div>
      </div>
      <div class="login-error" id="reset-error"></div>
      <button type="submit" class="btn btn-primary btn-block" id="reset-btn">Set new password</button>
    </form>
    <div class="login-switch"><a href="#/login">Back to sign in</a></div>`);

  const form = app.querySelector('#reset-form');
  bindPasswordToggle(form);

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const btn = app.querySelector('#reset-btn');
    const errEl = app.querySelector('#reset-error');
    // Passwords are never trimmed — leading/trailing spaces are part of them.
    const password = app.querySelector('#reset-password').value;
    const confirm = app.querySelector('#reset-confirm').value;

    errEl.textContent = '';
    if (password.length < MIN_PASSWORD_LENGTH) {
      errEl.textContent = `Password must be at least ${MIN_PASSWORD_LENGTH} characters`;
      return;
    }
    if (password !== confirm) {
      errEl.textContent = 'Passwords do not match';
      return;
    }

    btn.disabled = true;
    btn.textContent = 'Saving…';
    try {
      await resetPassword(token, password);
      showToast('Password updated — sign in with your new password', 'success');
      // Replace (not push) the history entry first, so Back can't return to a
      // form whose token is now spent.
      history.replaceState(null, '', '#/login');
      // The server issues no session on reset and revokes none, so a visitor
      // who was already signed in in this browser still is — and loginPage()'s
      // verifySession() bounce would send them to the dashboard under a toast
      // telling them to sign in. logout() ends that session, then its own
      // navigate() sees the hash already equals the target and force-resolves
      // #/login, so everyone lands on the sign-in form. POST /auth/logout has
      // no requireAuth, so this is safe for a signed-out visitor too.
      await logout();
    } catch (err) {
      // Dead link: nothing on this form can fix it, so it's a pane transition,
      // not an inline line. Anything else (429, network, …) stays inline and
      // the pane is NOT re-rendered — that would wipe the typed passwords.
      if (isDeadResetLink(err)) {
        renderDeadLink('This reset link is no longer valid — it may have expired or already been used.');
        return;
      }
      errEl.textContent = resetPasswordErrorText(err);
      btn.disabled = false;
      btn.textContent = 'Set new password';
    }
  });
}

export function resetPasswordPage(params) {
  teardownLayout();

  // decodeURIComponent throws on a malformed % escape; a link mangled that way
  // is a dead link like any other, so fall through to the shape check.
  let raw = (params && params.token) || '';
  try { raw = decodeURIComponent(raw); } catch { /* shape check below rejects it */ }

  const token = normalizeResetTokenInput(raw);
  if (!token) {
    renderDeadLink('This reset link is not valid.');
    return;
  }
  renderForm(token);
}
